type PageLoader = () => Promise<unknown>;

const CHUNK_RETRY_DELAY_MS = 1500;
const CHUNK_RELOAD_KEY = "lagoona:chunk-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

// At most one reload per cooldown, so a dead network cannot trigger a reload loop.
function reloadForFreshChunks(): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

// A page chunk can fail to download (network timeout, or a redeploy removed the old hashed file):
// retry once, then reload the page to fetch the current assets instead of crashing the app.
export function withChunkRetry<T>(load: () => Promise<T>): () => Promise<T> {
  return async () => {
    try {
      return await load();
    } catch {
      await new Promise((resolve) => setTimeout(resolve, CHUNK_RETRY_DELAY_MS));
    }
    try {
      return await load();
    } catch (error) {
      if (reloadForFreshChunks()) return new Promise<T>(() => {});
      throw error;
    }
  };
}

export const loadLoginPage = () => import("@/pages/LoginPage");
export const loadPublicProfilePage = () => import("@/pages/PublicProfilePage");
export const loadPublicAppleRepairPricingPage = () => import("@/pages/PublicAppleRepairPricingPage");
export const loadPublicBookingPage = () => import("@/pages/PublicBookingPage");
export const loadPublicManageBookingPage = () => import("@/pages/PublicManageBookingPage");
export const loadNotFoundPage = () => import("@/pages/NotFound");

export const loadEventTypesPage = () => import("@/pages/EventTypesPage");
export const loadEventFormPage = () => import("@/pages/EventFormPage");
export const loadBookingsPage = () => import("@/pages/BookingsPage");
export const loadEmailCampaignPage = () => import("@/pages/EmailCampaignPage");
export const loadClientsPage = () => import("@/pages/ClientsPage");
export const loadAnalyticsPage = () => import("@/pages/AnalyticsPage");
export const loadStatisticsPage = () => import("@/pages/StatisticsPage");
export const loadBillingPage = () => import("@/pages/BillingPage");
export const loadTransactionsPage = () => import("@/pages/TransactionsPage");
export const loadOperationDetailsPage = () => import("@/pages/OperationDetailsPage");
export const loadSitesPage = () => import("@/pages/SitesPage");
export const loadPointsOfSalePage = () => import("@/pages/PointsOfSalePage");
export const loadSettingsPage = () => import("@/pages/SettingsPage");
export const loadEmailTemplatesPage = () => import("@/pages/EmailTemplatesPage");
export const loadPublicLegalPage = () => import("@/pages/PublicLegalPage");
export const loadStatusPage = () => import("@/pages/StatusPage");
export const loadApiTesterPage = () => import("@/pages/ApiTesterPage");

const adminRouteLoaders: Array<[string, PageLoader]> = [
  ["/dashboard/events", loadEventTypesPage],
  ["/dashboard/bookings", loadBookingsPage],
  ["/dashboard/campaigns", loadEmailCampaignPage],
  ["/dashboard/emails", loadEmailTemplatesPage],
  ["/dashboard/clients", loadClientsPage],
  ["/dashboard/analytics", loadAnalyticsPage],
  ["/dashboard/statistics", loadStatisticsPage],
  ["/dashboard/billing", loadBillingPage],
  ["/dashboard/transactions", loadTransactionsPage],
  ["/dashboard/operations", loadOperationDetailsPage],
  ["/dashboard/upcoming-operations", loadOperationDetailsPage],
  ["/dashboard/sites", loadSitesPage],
  ["/dashboard/points-of-sale", loadPointsOfSalePage],
  ["/dashboard/settings", loadSettingsPage],
  ["/dashboard/status", loadStatusPage],
  ["/dashboard/api-tester", loadApiTesterPage],
];

export function preloadAdminRoute(pathname?: string) {
  if (!pathname) return;
  const match = adminRouteLoaders.find(([route]) => pathname.startsWith(route));
  if (match) void match[1]().catch(() => undefined);
}
